<script setup lang="ts">
import { ComboboxOptions, ListboxOptions } from '@headlessui/vue'
import type { Option } from './index.vue'

const props = defineProps<{
    autocomplete?: boolean
    options?: Option[]
    modelValue?: any
    direction?: 'left' | 'right' | 'top' | 'bottom'
    query?: string
    selectable?: boolean
    multiple?: boolean
    selectAllText?: string
    selectAllValue?: any
    nullable?: boolean
}>()

const emit = defineEmits<{
    (_e: 'update:modelValue', _value?: any): void
}>()

const filtered = computed(() => {
    const searchQuery = props.query?.toLowerCase()

    if (!searchQuery) return props.options

    return props.options?.filter(option => {
        return (
            !option.disabled &&
            option.label
                .toLowerCase()
                .concat(
                    (
                        option.groupLabel ??
                        option.group?.toString() ??
                        ''
                    ).toLowerCase()
                )
                .includes(searchQuery)
        )
    })
})

const byGroup = computed(() => {
    const groups = new Map<string | undefined, Option[]>()

    unref(filtered)?.forEach(option => {
        const name = option.groupLabel ?? option.group?.toString()

        if (!groups.has(name)) {
            groups.set(name, [])
        }

        groups.get(name)?.push(option)
    })

    return groups
})

function isGroupSelected(options: Option[]) {
    if (!props.multiple) return false

    return options.every(option => {
        return props.modelValue?.includes(option.value)
    })
}

const isAllSelected = computed(() => {
    if (!props.multiple) return false

    if (!props.selectAllValue?.length && !props.modelValue?.length) return true

    return props.selectAllValue?.every((value: any) => {
        return props.modelValue?.includes(value)
    })
})

function onSelectGroup(options: Option[]) {
    const currentValue = props.modelValue ?? []
    const isSelected = isGroupSelected(options)
    const optionsValues = options.map(option => option.value)
    const newValue = isSelected
        ? currentValue.filter((value: any) => !optionsValues.includes(value))
        : currentValue.concat(optionsValues)

    emit('update:modelValue', newValue)
}

function onSelectAll() {
    if (unref(isAllSelected)) {
        emit('update:modelValue', [])
    } else {
        emit('update:modelValue', props.selectAllValue)
    }
}
</script>

<template>
    <component
        :is="autocomplete ? ComboboxOptions : ListboxOptions"
        v-if="options"
        class="absolute z-[100] max-h-60 min-w-full max-w-xs overflow-auto rounded-sm border border-black bg-neutral py-1 text-base focus:outline-none sm:text-sm"
        :class="{
            'mt-1': direction === 'bottom',
            'bottom-full mb-1': direction === 'top',
            'right-0 top-0 ml-1': direction === 'right',
            'left-0 top-0 mr-1': direction === 'left'
        }"
    >
        <UiSelectOption
            v-if="selectAllText && !query"
            :option="{
                label: selectAllText
            }"
            :autocomplete="autocomplete"
            :selectable="selectable"
            :selected="isAllSelected"
            @select="onSelectAll"
        />

        <UiSelectOption
            v-if="nullable"
            :option="{
                label: '-',
                value: undefined
            }"
            :autocomplete="autocomplete"
            :selectable="selectable"
        />

        <template v-if="filtered?.length">
            <slot>
                <template v-for="[group, groupOptions] in byGroup" :key="group">
                    <slot
                        v-if="group"
                        :name="`group-${group}`"
                        :options="groupOptions"
                        :autocomplete="autocomplete"
                        :selectable="selectable"
                        :select="onSelectGroup"
                        :selected="isGroupSelected(groupOptions)"
                    >
                        <UiSelectOption
                            :option="{
                                label: group
                            }"
                            :autocomplete="autocomplete"
                            :selectable="selectable"
                            :selected="isGroupSelected(groupOptions)"
                            @select="onSelectGroup(groupOptions)"
                        />
                    </slot>

                    <UiSelectOption
                        v-for="(option, index) in groupOptions"
                        :key="index"
                        :class="group !== undefined ? 'pl-4' : ''"
                        :option="option"
                        :autocomplete="autocomplete"
                        :selectable="selectable"
                    />
                </template>
            </slot>
        </template>

        <template v-else-if="options">
            <li>
                <span
                    class="relative block select-none truncate py-2 font-normal"
                    :class="[selectable ? 'pl-10 pr-4' : 'px-4']"
                >
                    No results found
                </span>
            </li>
        </template>
    </component>
</template>